import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Sheet, SheetContent } from '../ui/sheet';
import {
  Search, MessageCircle, Loader2, ChevronDown, ArrowLeft, Calendar,
  Phone, IndianRupee, Filter, Send,
} from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { EVT } from '../../services/dataSyncBus';
import { useDataSync } from '../../hooks/useDataSync';
import { useRefetchOnFocus } from '../../hooks/useRefetchOnFocus';
import { useEmptyStateTracker } from '../../hooks/useEmptyStateTracker';
import { LastSyncedBadge } from './LastSyncedBadge';
import { track } from '../../services/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return s; }
};

// Date presets shared with Collections page
const datePresets = () => {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
  const last7 = new Date(today); last7.setDate(today.getDate() - 6);
  const last30 = new Date(today); last30.setDate(today.getDate() - 29);
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
  const prevMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const prevMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
  const last3 = new Date(today); last3.setMonth(today.getMonth() - 3);
  const last6 = new Date(today); last6.setMonth(today.getMonth() - 6);
  const prevYearStart = new Date(today.getFullYear() - 1, 0, 1);
  const prevYearEnd = new Date(today.getFullYear() - 1, 11, 31);
  return [
    { key: 'all',         label: 'All Time',         start: '', end: '' },
    { key: 'today',       label: 'Today',            start: fmt(today), end: fmt(today) },
    { key: 'yesterday',   label: 'Yesterday',        start: fmt(yesterday), end: fmt(yesterday) },
    { key: 'this_month',  label: today.toLocaleDateString('en-US', { month: 'long' }), start: fmt(monthStart), end: fmt(monthEnd) },
    { key: 'last_7',      label: 'Last 7 Days',      start: fmt(last7), end: fmt(today) },
    { key: 'last_30',     label: 'Last 30 Days',     start: fmt(last30), end: fmt(today) },
    { key: 'prev_month',  label: 'Previous Month',   start: fmt(prevMonthStart), end: fmt(prevMonthEnd) },
    { key: 'last_3m',     label: 'Last 3 Months',    start: fmt(last3), end: fmt(today) },
    { key: 'last_6m',     label: 'Last 6 Months',    start: fmt(last6), end: fmt(today) },
    { key: 'prev_year',   label: 'Previous Year',    start: fmt(prevYearStart), end: fmt(prevYearEnd) },
    { key: 'custom',      label: 'Custom Date Range', start: '', end: '' },
  ];
};

const DUE_TYPE_OPTS = [
  { key: 'rent',        label: 'Rent' },
  { key: 'deposit',     label: 'Security Deposit' },
  { key: 'electricity', label: 'Electricity' },
  { key: 'food',        label: 'Food / Mess' },
];
const DEFAULTER_OPTS = [
  { key: 'all',        label: 'Anyone with dues' },
  { key: 'gt_30',      label: 'Overdue > 30 days' },
  { key: 'gt_60',      label: 'Overdue > 60 days' },
  { key: 'gt_90',      label: 'Overdue > 90 days' },
];
const TENANT_TYPE_OPTS = [
  { key: 'active', label: 'Active' },
  { key: 'notice', label: 'Under Notice' },
];

const Dues = () => {
  const { token, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkSending, setBulkSending] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  // Filters — honor ?range= and ?filter= URL hints on first paint so that
  // KPI clicks from /manager/home land on the correct pre-filtered view.
  const [searchParams] = useSearchParams();
  const initialFilters = useMemo(() => {
    const rangeKey = searchParams.get('range');
    const filterHint = searchParams.get('filter');
    const presets = datePresets();
    const presetMap = { today: 'today', month: 'this_month', yesterday: 'yesterday' };
    const wantedRange = presetMap[rangeKey];
    const datePreset = wantedRange ? presets.find(p => p.key === wantedRange) : null;

    return {
      dateRange: datePreset
        ? { preset: datePreset.key, start: datePreset.start, end: datePreset.end }
        : { preset: 'all', start: '', end: '' },
      addedBy: [],
      // ?filter=unpaid_deposit narrows the due-type chip to "Security Deposit".
      dueType: filterHint === 'unpaid_deposit' ? ['deposit'] : [],
      // ?filter=defaulters maps to the broadest defaulter bucket (>30 days
      // overdue). The user can tighten to >60/>90 from the filter sheet.
      defaulter: filterHint === 'defaulters' ? 'gt_30' : 'all',
      tenantType: [],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [filters, setFilters] = useState(initialFilters);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const fetchDues = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/money/dues-summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(r.data);
      setLastSyncedAt(new Date());
    } catch (e) {
      toast({
        title: 'Could not load dues',
        description: e.response?.data?.detail || '',
        variant: 'destructive',
      });
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchDues(); }, [fetchDues]);

  // Hot refresh on any payment or tenant change anywhere in the app
  useDataSync([EVT.PAYMENT, EVT.TENANT], fetchDues);
  // Fallback: also refetch when the tab regains focus (catches the case where
  // BroadcastChannel was throttled while the tab was backgrounded).
  useRefetchOnFocus(fetchDues);
  useEmptyStateTracker('dues', data?.tenant_dues?.length || 0, loading);

  // Apply filters
  const filteredRows = useMemo(() => {
    const rows = data?.tenant_dues || [];
    return rows.filter(r => {
      // Search
      if (search) {
        const q = search.toLowerCase();
        const blob = [r.tenant_name, r.room_number, r.property_name, r.tenant_phone].filter(Boolean).join(' ').toLowerCase();
        if (!blob.includes(q)) return false;
      }
      // Date range — filter by oldest_due_date
      if (filters.dateRange.start && (r.oldest_due_date || '') < filters.dateRange.start) return false;
      if (filters.dateRange.end   && (r.oldest_due_date || '') > filters.dateRange.end)   return false;
      // Due Type — must intersect
      if (filters.dueType.length) {
        const hasMatch = (r.due_types || []).some(t => filters.dueType.includes(t));
        if (!hasMatch) return false;
      }
      // Defaulter
      if (filters.defaulter !== 'all') {
        const days = r.oldest_due_date ? Math.floor((Date.now() - new Date(r.oldest_due_date)) / 86400000) : 0;
        const min = { gt_30: 30, gt_60: 60, gt_90: 90 }[filters.defaulter] || 0;
        if (days <= min) return false;
      }
      return true;
    });
  }, [data, search, filters]);

  const totalShown = filteredRows.reduce((s, r) => s + (r.amount || 0), 0);

  const activeFilterCount =
    (filters.dateRange.preset !== 'all' && filters.dateRange.preset ? 1 : 0) +
    (filters.addedBy.length ? 1 : 0) +
    (filters.dueType.length ? 1 : 0) +
    (filters.defaulter !== 'all' ? 1 : 0) +
    (filters.tenantType.length ? 1 : 0);

  const clearAll = () => {
    setFilters({
      dateRange: { preset: 'all', start: '', end: '' },
      addedBy: [],
      dueType: [],
      defaulter: 'all',
      tenantType: [],
    });
    setSearch('');
  };

  // ----- Bulk select / WA reminders -----
  const toggleSelect = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => {
    if (selectedIds.length === filteredRows.length) setSelectedIds([]);
    else setSelectedIds(filteredRows.map(r => r.tenant_id));
  };

  const sendOne = async (tenantId) => {
    setSendingId(tenantId);
    try {
      await axios.post(`${API}/whatsapp/send-rent-reminder`, { tenant_id: tenantId },
        { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Reminder sent on WhatsApp' });
    } catch (e) {
      toast({ title: 'Failed', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSendingId(null); }
  };

  const sendBulk = async () => {
    const ids = selectedIds.length ? selectedIds : filteredRows.map(r => r.tenant_id);
    if (!ids.length) {
      toast({ title: 'No tenants to remind', variant: 'destructive' });
      return;
    }
    setBulkSending(true);
    let sent = 0; let failed = 0;
    for (const id of ids) {
      try {
        await axios.post(`${API}/whatsapp/send-rent-reminder`, { tenant_id: id },
          { headers: { Authorization: `Bearer ${token}` } });
        sent += 1;
      } catch { failed += 1; }
    }
    toast({
      title: `Reminders queued`,
      description: `${sent} sent, ${failed} failed.`,
    });
    track('wa_reminder_sent', { surface: 'dues_bulk', sent_count: sent, failed_count: failed, total_targeted: ids.length });
    setBulkSending(false);
    setSelectedIds([]);
  };

  // KPI tiles
  const KPI = [
    { key: 'all_dues',              label: 'All Dues' },
    { key: 'current_dues',          label: 'Current Dues' },
    { key: 'month_dues',            label: `${data?.month_label || 'This Month'} Dues` },
    { key: 'total_future_dues',     label: 'Total Future Dues' },
    { key: 'month_rent_dues',       label: `${data?.month_label || 'This Month'} Rent Dues` },
    { key: 'month_electricity_dues',label: `${data?.month_label || 'This Month'} Electricity Bill Dues` },
    { key: 'month_food_dues',       label: `${data?.month_label || 'This Month'} Mess Food Dues` },
    { key: 'month_deposit_dues',    label: `${data?.month_label || 'This Month'} Cash Deposit Dues` },
    { key: 'total_unpaid_rent',     label: 'Total Unpaid Rent' },
    { key: 'total_electricity_dues',label: 'Total Electricity Bill Dues' },
  ];

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto" />
          <p className="mt-3 text-gray-600 text-sm">Loading dues...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900">Dues</h1>
        <div className="flex items-center gap-2">
          <LastSyncedBadge syncedAt={lastSyncedAt} onRefresh={fetchDues} refreshing={loading} />
          <Button
            onClick={() => { track('cta_dues_wa_bulk_clicked', { selected_count: selectedIds.length }); sendBulk(); }}
            disabled={bulkSending}
            variant="outline"
            className="border-green-500 text-green-700 hover:bg-green-50 font-medium"
            data-testid="bulk-wa-reminder-btn"
          >
            {bulkSending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <MessageCircle size={14} className="mr-1.5" />}
            WhatsApp Bulk Reminder
            {selectedIds.length > 0 && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-green-600 text-white text-[10px] font-bold">{selectedIds.length}</span>}
          </Button>
        </div>
      </div>

      {/* KPI strip — 10 red cards, horizontal scroll */}
      <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory" data-testid="dues-kpi-strip">
        {KPI.map(k => (
          <Card key={k.key} data-testid={`dues-kpi-${k.key}`}
            className="p-4 min-w-[150px] snap-start border border-gray-200 hover:border-red-300 transition-colors">
            <p className="text-xl font-bold text-red-600 tabular-nums leading-none">
              {inr(data?.kpi_summary?.[k.key])}
            </p>
            <p className="text-[11px] text-gray-700 font-medium mt-2 leading-tight">{k.label}</p>
          </Card>
        ))}
      </div>

      {/* Search + Filter chip strip */}
      <Card className="p-4 border border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
          <Input
            placeholder="Search tenants with dues…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="dues-search"
          />
        </div>

        <div className="flex items-center gap-2 mt-3 overflow-x-auto pb-1 scrollbar-hide" data-testid="dues-chip-strip">
          <DateChip
            testId="chip-dateRange"
            value={filters.dateRange}
            onChange={(v) => { track('dues_filter_applied', { filter: 'date_range', preset: v.preset }); setFilters(f => ({ ...f, dateRange: v })); }}
            label={
              filters.dateRange.preset === 'all' || !filters.dateRange.preset
                ? 'Date Range'
                : (datePresets().find(x => x.key === filters.dateRange.preset)?.label || 'Custom')
            }
            highlight={filters.dateRange.preset !== 'all' && filters.dateRange.preset !== ''}
          />

          <ChipPopover
            testId="chip-addedBy"
            label="Added By"
            highlight={filters.addedBy.length > 0}
          >
            <p className="text-xs text-gray-500 px-2 py-2">
              Currently: {user?.name || 'You'}
            </p>
          </ChipPopover>

          <MultiChip
            testId="chip-dueType"
            label={filters.dueType.length === 0 ? 'Due Type' :
                   filters.dueType.length === 1 ? DUE_TYPE_OPTS.find(o => o.key === filters.dueType[0])?.label : `${filters.dueType.length} selected`}
            options={DUE_TYPE_OPTS}
            selected={filters.dueType}
            onChange={(arr) => { track('dues_filter_applied', { filter: 'due_type', count: arr.length }); setFilters(f => ({ ...f, dueType: arr })); }}
            highlight={filters.dueType.length > 0}
          />

          <SingleChip
            testId="chip-defaulter"
            label={DEFAULTER_OPTS.find(o => o.key === filters.defaulter)?.label || 'Defaulter'}
            options={DEFAULTER_OPTS}
            value={filters.defaulter}
            onChange={(v) => setFilters(f => ({ ...f, defaulter: v }))}
            highlight={filters.defaulter !== 'all'}
          />

          <MultiChip
            testId="chip-tenantType"
            label={filters.tenantType.length === 0 ? 'Tenant Type' :
                   filters.tenantType.length === 1 ? TENANT_TYPE_OPTS.find(o => o.key === filters.tenantType[0])?.label : `${filters.tenantType.length} selected`}
            options={TENANT_TYPE_OPTS}
            selected={filters.tenantType}
            onChange={(arr) => setFilters(f => ({ ...f, tenantType: arr }))}
            highlight={filters.tenantType.length > 0}
          />

          <button
            data-testid="open-dues-filters"
            onClick={() => setSheetOpen(true)}
            className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-full border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 whitespace-nowrap"
          >
            <Filter size={14} /> Filters
            {activeFilterCount > 0 && (
              <span className="ml-0.5 inline-flex items-center justify-center w-5 h-5 text-[10px] rounded-full bg-orange-600 text-white font-bold">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>
      </Card>

      {/* Results header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-700" data-testid="dues-results-count">
          <span className="font-bold text-red-600">{inr(totalShown)}</span>
          <span className="mx-1.5 text-gray-400">/</span>
          <span className="font-medium">{filteredRows.length} {filteredRows.length === 1 ? 'Result' : 'Results'} Found</span>
        </p>
        {(activeFilterCount > 0 || search) && (
          <button onClick={clearAll} data-testid="dues-clear-all" className="text-sm font-medium text-orange-700 hover:text-orange-800 underline">
            Clear All
          </button>
        )}
      </div>

      {/* Table */}
      <Card className="overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="dues-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    data-testid="select-all-dues"
                    checked={filteredRows.length > 0 && selectedIds.length === filteredRows.length}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Tenant</th>
                <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Room</th>
                <th className="px-3 py-3 text-right text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Oldest Due Date</th>
                <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Latest Due Date</th>
                <th className="px-3 py-3 text-left text-[11px] font-semibold text-gray-600 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center p-12 text-gray-500" data-testid="dues-empty">
                    No data available
                  </td>
                </tr>
              ) : (
                filteredRows.map(r => (
                  <tr key={r.tenant_id} className="hover:bg-orange-50/30" data-testid={`due-row-${r.tenant_id}`}>
                    <td className="px-3 py-2.5">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(r.tenant_id)}
                        onChange={() => toggleSelect(r.tenant_id)}
                      />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-[11px] font-bold">
                          {(r.tenant_name || '?').charAt(0)}
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-900 truncate max-w-[160px]">{r.tenant_name}</p>
                          {r.due_types?.length > 0 && (
                            <p className="text-[10px] text-gray-500 capitalize">{r.due_types.join(', ')}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-700">
                      {r.room_number ? `Room ${r.room_number}` : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <span className="text-sm font-bold text-red-600 tabular-nums">{inr(r.amount)}</span>
                    </td>
                    <td className="px-3 py-2.5 text-xs text-gray-700 whitespace-nowrap">{fmtDate(r.oldest_due_date)}</td>
                    <td className="px-3 py-2.5 text-xs text-gray-700 whitespace-nowrap">{fmtDate(r.latest_due_date)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-[11px] px-2 border-green-500 text-green-700 hover:bg-green-50"
                          disabled={sendingId === r.tenant_id}
                          onClick={() => sendOne(r.tenant_id)}
                          data-testid={`due-remind-${r.tenant_id}`}
                        >
                          {sendingId === r.tenant_id ? <Loader2 size={11} className="animate-spin mr-1" /> : <Send size={11} className="mr-1" />}
                          Remind
                        </Button>
                        {r.tenant_phone && (
                          <a
                            href={`tel:${r.tenant_phone}`}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-50"
                            title={r.tenant_phone}
                          >
                            <Phone size={11} />
                          </a>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Right slide-in filter sheet */}
      <DuesFiltersSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        filters={filters}
        setFilters={setFilters}
        onClear={clearAll}
      />
    </div>
  );
};

// ============================================================
// Reusable chips (DateChip, MultiChip, SingleChip, ChipPopover)
// ============================================================
const DateChip = ({ value, onChange, label, highlight, testId }) => {
  const [open, setOpen] = useState(false);
  const presets = datePresets();
  const [custom, setCustom] = useState({ start: value.start, end: value.end });
  useEffect(() => { setCustom({ start: value.start, end: value.end }); }, [value]);
  const apply = (preset) => {
    if (preset.key === 'custom') onChange({ preset: 'custom', start: custom.start || '', end: custom.end || '' });
    else onChange({ preset: preset.key, start: preset.start, end: preset.end });
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testId}
          className={`inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
            highlight
              ? 'bg-orange-50 border-orange-300 text-orange-800 font-medium'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          <Calendar size={12} /> {label}
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-2" align="start">
        <div className="max-h-72 overflow-y-auto">
          {presets.map(p => (
            <button
              key={p.key}
              onClick={() => apply(p)}
              className={`w-full text-left text-sm px-2.5 py-1.5 rounded hover:bg-orange-50 ${
                value.preset === p.key ? 'bg-orange-100 text-orange-800 font-medium' : 'text-gray-700'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {value.preset === 'custom' && (
          <div className="border-t mt-2 pt-2 grid grid-cols-2 gap-2">
            <input type="date" value={custom.start} onChange={(e) => setCustom(c => ({ ...c, start: e.target.value }))}
              className="text-xs border border-gray-300 rounded px-2 py-1" />
            <input type="date" value={custom.end} onChange={(e) => setCustom(c => ({ ...c, end: e.target.value }))}
              className="text-xs border border-gray-300 rounded px-2 py-1" />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const MultiChip = ({ label, options, selected, onChange, highlight, testId }) => {
  const [open, setOpen] = useState(false);
  const toggle = (k) => onChange(selected.includes(k) ? selected.filter(x => x !== k) : [...selected, k]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testId}
          className={`inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
            highlight
              ? 'bg-orange-50 border-orange-300 text-orange-800 font-medium'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {label}
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        {options.map(opt => (
          <label key={opt.key} className="flex items-center gap-2 px-2.5 py-1.5 rounded hover:bg-orange-50 cursor-pointer text-sm text-gray-700">
            <input type="checkbox" checked={selected.includes(opt.key)} onChange={() => toggle(opt.key)} className="accent-orange-600" />
            {opt.label}
          </label>
        ))}
        {selected.length > 0 && (
          <button onClick={() => onChange([])} className="w-full text-center text-xs text-gray-500 hover:text-orange-700 py-1.5 border-t mt-1">
            Clear
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
};

const SingleChip = ({ label, options, value, onChange, highlight, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testId}
          className={`inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
            highlight
              ? 'bg-orange-50 border-orange-300 text-orange-800 font-medium'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {label}
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">
        {options.map(opt => (
          <button
            key={opt.key}
            onClick={() => { onChange(opt.key); setOpen(false); }}
            className={`w-full text-left text-sm px-2.5 py-1.5 rounded hover:bg-orange-50 ${
              value === opt.key ? 'bg-orange-100 text-orange-800 font-medium' : 'text-gray-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
};

const ChipPopover = ({ label, highlight, testId, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid={testId}
          className={`inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
            highlight
              ? 'bg-orange-50 border-orange-300 text-orange-800 font-medium'
              : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
          }`}
        >
          {label}
          <ChevronDown size={12} className="opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-1" align="start">{children}</PopoverContent>
    </Popover>
  );
};

// Right-side filter sheet
const DUES_SECTIONS = [
  { key: 'dateRange',  label: 'Date Range',  kind: 'date' },
  { key: 'dueType',    label: 'Due Type',    kind: 'multi', options: DUE_TYPE_OPTS },
  { key: 'defaulter',  label: 'Defaulter',   kind: 'single', options: DEFAULTER_OPTS },
  { key: 'tenantType', label: 'Tenant Type', kind: 'multi', options: TENANT_TYPE_OPTS },
];

const DuesFiltersSheet = ({ open, onClose, filters, setFilters, onClear }) => {
  const [active, setActive] = useState('dateRange');
  const [draft, setDraft] = useState(filters);
  useEffect(() => { if (open) setDraft(filters); }, [open, filters]);
  const apply = () => { setFilters(draft); onClose(); };
  const clearAndClose = () => { onClear(); onClose(); };
  const section = DUES_SECTIONS.find(s => s.key === active) || DUES_SECTIONS[0];
  const setKey = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const selectedCount = (k) => {
    const v = draft[k];
    if (Array.isArray(v)) return v.length;
    if (v && v.preset && v.preset !== 'all') return 1;
    if (typeof v === 'string' && v !== 'all') return 1;
    return 0;
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col" data-testid="dues-filters-sheet">
        <div className="flex items-center gap-2 px-4 py-3 border-b">
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><ArrowLeft size={16} /></button>
          <h2 className="text-base font-semibold">Due Filters</h2>
        </div>
        <div className="flex flex-1 overflow-hidden">
          <div className="w-44 sm:w-52 border-r bg-gray-50 overflow-y-auto">
            {DUES_SECTIONS.map(s => {
              const cnt = selectedCount(s.key);
              const a = active === s.key;
              return (
                <button key={s.key} onClick={() => setActive(s.key)}
                  className={`w-full text-left px-3 py-3 text-sm border-l-4 ${
                    a ? 'bg-white border-orange-600 text-gray-900 font-medium' : 'border-transparent text-gray-600 hover:bg-white'
                  }`}>
                  <div>{s.label}</div>
                  {cnt > 0 && <div className="text-[10px] text-orange-700 mt-0.5">{cnt} selected</div>}
                </button>
              );
            })}
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">{section.label}</p>
            {section.kind === 'date' && (
              <div className="space-y-1">
                {datePresets().map(p => (
                  <label key={p.key} className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer hover:bg-orange-50 ${
                    draft.dateRange.preset === p.key ? 'bg-orange-50' : ''
                  }`}>
                    <input type="checkbox" checked={draft.dateRange.preset === p.key}
                      onChange={() => setKey('dateRange', { preset: p.key, start: p.start, end: p.end })}
                      className="accent-orange-600" />
                    {p.label}
                  </label>
                ))}
              </div>
            )}
            {section.kind === 'multi' && (
              <div className="space-y-1">
                {section.options.map(opt => {
                  const selected = (draft[section.key] || []).includes(opt.key);
                  return (
                    <label key={opt.key} className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer hover:bg-orange-50 ${selected ? 'bg-orange-50' : ''}`}>
                      <input type="checkbox" checked={selected}
                        onChange={() => {
                          const cur = draft[section.key] || [];
                          setKey(section.key, selected ? cur.filter(x => x !== opt.key) : [...cur, opt.key]);
                        }}
                        className="accent-orange-600" />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            )}
            {section.kind === 'single' && (
              <div className="space-y-1">
                {section.options.map(opt => (
                  <label key={opt.key} className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer hover:bg-orange-50 ${
                    draft[section.key] === opt.key ? 'bg-orange-50' : ''
                  }`}>
                    <input type="radio" checked={draft[section.key] === opt.key}
                      onChange={() => setKey(section.key, opt.key)}
                      className="accent-orange-600" />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="border-t p-3 flex gap-3">
          <Button variant="outline" className="flex-1" onClick={clearAndClose}>Clear All</Button>
          <Button className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" onClick={apply}>Apply</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default Dues;
